import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../services/useAuth";
import { useTitle } from "../../../hooks/useTitle";
import { translate } from "../../../hooks/translate";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Button from "../../../components/BaseUI/Button";
import Lozenge from "../../../components/BaseUI/Lozenge";
import DatePicker from "../../../components/CustomUI/DatePicker";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  FiActivity,
  FiRefreshCw,
  FiArrowLeft,
  FiUser,
  FiServer,
  FiMapPin,
  FiClock,
  FiFileText,
  FiExternalLink,
  FiChevronLeft,
  FiChevronRight,
  FiCalendar,
  FiX,
} from "react-icons/fi";
import "./Activities.scss";

const API_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:3100";

const ActivityDetails = () => {
  const { type, id } = useParams();
  const navigate = useNavigate();
  const { getToken } = useAuth();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [limit] = useState(20);
  const [toasts, setToasts] = useState([]);
  const [entity, setEntity] = useState(null);
  const [periodView, setPeriodView] = useState("day");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [customDateRange, setCustomDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [tempDateRange, setTempDateRange] = useState({
    startDate: null,
    endDate: null,
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  // Drill-down: machine → list users first; user → list machines first. Click item → activities.
  const [viewMode, setViewMode] = useState("users"); // 'activities' | 'users' | 'machines'
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [selectedMachineId, setSelectedMachineId] = useState(null);
  const [usersList, setUsersList] = useState([]);
  const [machinesList, setMachinesList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const initialLoadDone = useRef(false);

  useTitle(
    `Rovion MES - ${type === "user" ? "Kullanıcı" : "Makine"} Aktiviteleri`,
  );

  const addToast = (message, type = "info") => {
    const newToast = {
      id: Date.now(),
      message,
      type,
    };
    setToasts([...toasts, newToast]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== newToast.id));
    }, 3000);
  };

  const getActivityTypeLabel = (type) => {
    const labels = {
      start: "Başlatıldı",
      stop: "Durduruldu",
      pause: "Duraklatıldı",
      resume: "Devam Edildi",
    };
    return labels[type] || type;
  };

  const getActivityTypeVariant = (type) => {
    const variants = {
      start: "success",
      stop: "danger",
      pause: "warning",
      resume: "info",
    };
    return variants[type] || "default";
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Convert Date object to DD/MM/YYYY
  const formatDateForPicker = (date) => {
    if (!date) return "";
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Convert DD/MM/YYYY to Date object
  const parseDateFromPicker = (dateString) => {
    if (!dateString) return null;
    const [day, month, year] = dateString.split("/");
    return new Date(year, month - 1, day);
  };

  // Navigate to previous day
  const handlePreviousDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() - 1);
    setSelectedDate(newDate);
  };

  // Navigate to next day
  const handleNextDay = () => {
    const newDate = new Date(selectedDate);
    newDate.setDate(newDate.getDate() + 1);
    // Don't allow future dates
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    if (newDate <= today) {
      setSelectedDate(newDate);
    }
  };

  // Handle custom date range selection
  const handleCustomPeriodSelect = () => {
    setTempDateRange({
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    });
    setShowDatePicker(true);
  };

  const handleStartDateChange = (dateString) => {
    const date = parseDateFromPicker(dateString);
    setTempDateRange((prev) => ({
      ...prev,
      startDate: date,
    }));
  };

  const handleEndDateChange = (dateString) => {
    const date = parseDateFromPicker(dateString);
    setTempDateRange((prev) => ({
      ...prev,
      endDate: date,
    }));
  };

  const applyDateRange = () => {
    if (tempDateRange.startDate && tempDateRange.endDate) {
      setCustomDateRange({
        startDate: tempDateRange.startDate,
        endDate: tempDateRange.endDate,
      });
      setPeriodView("custom");
      setShowDatePicker(false);
    }
  };

  const clearCustomDateRange = () => {
    setCustomDateRange({ startDate: null, endDate: null });
    setTempDateRange({ startDate: null, endDate: null });
    setPeriodView("day");
    setShowDatePicker(false);
  };

  const cancelDateRange = () => {
    setTempDateRange({
      startDate: customDateRange.startDate,
      endDate: customDateRange.endDate,
    });
    if (!customDateRange.startDate || !customDateRange.endDate) {
      setPeriodView("day");
    }
    setShowDatePicker(false);
  };

  // Get date range for API params
  const getDateRangeParams = () => {
    const params = {};

    // If "all" is selected, don't apply any date filter
    if (periodView === "all") {
      return params;
    }

    const now = new Date();
    let startDate = null;
    let endDate = null;

    if (periodView === "day") {
      const dayDate = new Date(selectedDate);
      startDate = new Date(dayDate);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date(dayDate);
      endDate.setHours(23, 59, 59, 999);
    } else if (periodView === "week") {
      const dayOfWeek = now.getDay();
      startDate = new Date();
      startDate.setDate(now.getDate() - dayOfWeek);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
    } else if (periodView === "month") {
      startDate = new Date();
      startDate.setDate(1);
      startDate.setHours(0, 0, 0, 0);
      endDate = new Date();
      endDate.setHours(23, 59, 59, 999);
    } else if (periodView === "custom") {
      if (customDateRange.startDate && customDateRange.endDate) {
        startDate = new Date(customDateRange.startDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(customDateRange.endDate);
        endDate.setHours(23, 59, 59, 999);
      } else {
        return params; // No date filter if custom range not set
      }
    }

    // Only add date params if we have valid dates
    if (startDate && endDate) {
      params.startDate = startDate.toISOString();
      params.endDate = endDate.toISOString();
    }

    return params;
  };

  const fetchActivities = async (pageNumber = 1) => {
    try {
      setLoading(true);

      const params = {
        page: pageNumber,
        limit: limit,
      };

      if (type === "user") {
        params.userId = id;
        if (selectedMachineId) params.machineId = selectedMachineId;
      } else if (type === "machine") {
        params.machineId = id;
        if (selectedUserId) params.userId = selectedUserId;
      }

      // Add date range filters
      const dateParams = getDateRangeParams();
      Object.assign(params, dateParams);

      const token = getToken();
      const response = await axios.get(`${API_URL}/api/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });

      const data = response.data?.data || response.data || response;
      const items = data.activities || [];
      const pagination = data.pagination;

      setActivities(items);

      if (items.length > 0) {
        if (type === "user") {
          setEntity(items[0].user);
        } else if (type === "machine") {
          setEntity(items[0].machine);
        }
      }

      if (pagination) {
        setCurrentPage(pagination.page || pageNumber);
        setTotalPages(pagination.pages || 1);
        setTotalItems(pagination.total || items.length);
      } else {
        setCurrentPage(pageNumber);
        setTotalPages(1);
        setTotalItems(items.length);
      }
    } catch (error) {
      console.error("Failed to fetch activities:", error);
      addToast("Aktiviteler yüklenemedi", "danger");
      setActivities([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!type || !id) return;
    initialLoadDone.current = false;
    setCurrentPage(1);
    setSelectedUserId(null);
    setSelectedMachineId(null);
    if (type === "machine") {
      setViewMode("users");
      fetchUsersForMachine();
    } else if (type === "user") {
      setViewMode("machines");
      fetchMachinesForUser();
    }
  }, [type, id]);

  useEffect(() => {
    if (!type || !id) return;
    if (!initialLoadDone.current) {
      initialLoadDone.current = true;
      return;
    }
    if (viewMode === "users" && type === "machine") fetchUsersForMachine();
    else if (viewMode === "machines" && type === "user") fetchMachinesForUser();
    else if (viewMode === "activities") fetchActivities(1);
  }, [
    periodView,
    selectedDate,
    customDateRange,
    selectedUserId,
    selectedMachineId,
    viewMode,
  ]);

  const fetchUsersForMachine = async () => {
    if (type !== "machine" || !id) return;
    setLoadingList(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          machineId: id,
          limit: 5000,
          page: 1,
          ...getDateRangeParams(),
        },
      });
      const items = res.data?.data?.activities || [];
      const userMap = new Map(); // uid -> { user, count }
      for (const a of items) {
        if (!a.user) continue;
        const uid = String(a.user.id || a.user._id);
        if (!uid) continue;
        const existing = userMap.get(uid);
        if (existing) {
          existing.count += 1;
        } else {
          userMap.set(uid, { user: { ...a.user, id: uid }, count: 1 });
        }
      }

      const users = Array.from(userMap.values()).map((x) => ({
        ...x.user,
        recordCount: x.count,
      }));
      setUsersList(users);
      setViewMode("users");
    } catch (e) {
      console.error("Failed to fetch users for machine:", e);
      addToast("Kullanıcılar yüklenemedi", "danger");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchMachinesForUser = async () => {
    if (type !== "user" || !id) return;
    setLoadingList(true);
    try {
      const token = getToken();
      const res = await axios.get(`${API_URL}/api/activities`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { userId: id, limit: 5000, page: 1, ...getDateRangeParams() },
      });
      const items = res.data?.data?.activities || [];
      const machineMap = new Map(); // mid -> { machine, count }
      for (const a of items) {
        if (!a.machine) continue;
        const mid = String(a.machine.id || a.machine._id);
        if (!mid) continue;
        const existing = machineMap.get(mid);
        if (existing) {
          existing.count += 1;
        } else {
          machineMap.set(mid, { machine: { ...a.machine, id: mid }, count: 1 });
        }
      }

      const machines = Array.from(machineMap.values()).map((x) => ({
        ...x.machine,
        recordCount: x.count,
      }));
      setMachinesList(machines);
      setViewMode("machines");
    } catch (e) {
      console.error("Failed to fetch machines for user:", e);
      addToast("Makineler yüklenemedi", "danger");
    } finally {
      setLoadingList(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages || newPage === currentPage) return;
    setCurrentPage(newPage);
    fetchActivities(newPage);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 5; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }

    return pages;
  };

  const getTitle = () => {
    if (type === "user") {
      return entity?.name || "Kullanıcı Aktiviteleri";
    } else if (type === "machine") {
      return entity?.name || "Makine Aktiviteleri";
    }
    return "Aktiviteler";
  };

  const getSubtitle = () => {
    if (type === "user") {
      return entity?.email || "";
    } else if (type === "machine") {
      return entity?.code
        ? `${entity.code}${entity.location ? ` • ${entity.location}` : ""}`
        : "";
    }
    return "";
  };

  return (
    <AppPage title={getTitle()} subtitle={getSubtitle()} icon={<FiActivity />}>
      <CardContainer className="p-4">
        <div className="activities-header">
          <div className="actions-container mb-3">
            <Button
              appearance="danger-subtle"
              onClick={() => {
                if (viewMode === "activities") {
                  if (type === "machine") {
                    setSelectedUserId(null);
                    setViewMode("users");
                  } else {
                    setSelectedMachineId(null);
                    setViewMode("machines");
                  }
                } else {
                  navigate("/activities");
                }
              }}
              icon={<FiArrowLeft />}
            >
              Geri
            </Button>
          </div>
          <div className="period-selector">
            {periodView === "custom" &&
              customDateRange.startDate &&
              customDateRange.endDate && (
                <div className="custom-date-badge">
                  <span>
                    {formatDateForPicker(customDateRange.startDate)} -{" "}
                    {formatDateForPicker(customDateRange.endDate)}
                  </span>
                  <button
                    className="clear-badge-btn"
                    onClick={clearCustomDateRange}
                    title={translate("UI.CLEAR_DATE_RANGE")}
                  >
                    <FiX size={12} />
                  </button>
                </div>
              )}
            {periodView === "day" && (
              <div className="day-navigation">
                <button
                  className="nav-day-btn"
                  onClick={handlePreviousDay}
                  title={translate("UI.PREVIOUS_DAY")}
                >
                  <FiChevronLeft size={16} />
                </button>
                <span className="selected-date-label">
                  {(() => {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    const selected = new Date(selectedDate);
                    selected.setHours(0, 0, 0, 0);
                    if (selected.getTime() === today.getTime()) {
                      return translate("UI.TODAY");
                    }
                    return formatDateForPicker(selectedDate);
                  })()}
                </span>
                <button
                  className="nav-day-btn"
                  onClick={handleNextDay}
                  disabled={
                    new Date(selectedDate).toDateString() ===
                    new Date().toDateString()
                  }
                  title={translate("UI.NEXT_DAY")}
                >
                  <FiChevronRight size={16} />
                </button>
              </div>
            )}
            <button
              className={`period-btn ${periodView === "all" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("all");
                setShowDatePicker(false);
              }}
              title="Tümü"
            >
              Tümü
            </button>
            <button
              className={`period-btn ${periodView === "day" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("day");
                setShowDatePicker(false);
              }}
              title={translate("UI.DAY")}
            >
              {translate("UI.DAY")}
            </button>
            <button
              className={`period-btn ${periodView === "week" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("week");
                setShowDatePicker(false);
              }}
              title={translate("UI.THIS_WEEK")}
            >
              {translate("UI.WEEK")}
            </button>
            <button
              className={`period-btn ${periodView === "month" ? "active" : ""}`}
              onClick={() => {
                setPeriodView("month");
                setShowDatePicker(false);
              }}
              title={translate("UI.THIS_MONTH")}
            >
              {translate("UI.MONTH")}
            </button>
            <button
              className={`period-btn ${
                periodView === "custom" ? "active" : ""
              }`}
              onClick={handleCustomPeriodSelect}
              title={translate("UI.CUSTOM_DATE_RANGE")}
            >
              <FiCalendar size={14} /> {translate("UI.CUSTOM")}
            </button>
          </div>
          {showDatePicker && (
            <div className="custom-date-range-picker">
              <div className="date-range-header">
                <span>{translate("UI.SELECT_DATE_RANGE")}</span>
                <button className="close-date-picker" onClick={cancelDateRange}>
                  <FiX size={16} />
                </button>
              </div>
              <div className="date-range-inputs">
                <div className="date-input-group">
                  <DatePicker
                    label={translate("UI.START_DATE")}
                    placeholder={translate("UI.SELECT_START_DATE")}
                    value={
                      tempDateRange.startDate
                        ? formatDateForPicker(tempDateRange.startDate)
                        : ""
                    }
                    maxDate={
                      tempDateRange.endDate
                        ? formatDateForPicker(tempDateRange.endDate)
                        : formatDateForPicker(new Date())
                    }
                    onChange={handleStartDateChange}
                  />
                </div>
                <div className="date-input-group">
                  <DatePicker
                    label={translate("UI.END_DATE")}
                    placeholder={translate("UI.SELECT_END_DATE")}
                    value={
                      tempDateRange.endDate
                        ? formatDateForPicker(tempDateRange.endDate)
                        : ""
                    }
                    minDate={
                      tempDateRange.startDate
                        ? formatDateForPicker(tempDateRange.startDate)
                        : undefined
                    }
                    maxDate={formatDateForPicker(new Date())}
                    onChange={handleEndDateChange}
                  />
                </div>
                <div className="date-range-actions">
                  <button
                    className="apply-date-range-btn"
                    onClick={applyDateRange}
                    disabled={
                      !tempDateRange.startDate || !tempDateRange.endDate
                    }
                    title={translate("UI.APPLY_DATE_RANGE")}
                  >
                    {translate("UI.APPLY")}
                  </button>
                  {(tempDateRange.startDate || tempDateRange.endDate) && (
                    <button
                      className="clear-date-range-btn"
                      onClick={clearCustomDateRange}
                      title={translate("UI.CLEAR_DATE_RANGE")}
                    >
                      <FiX size={14} /> {translate("UI.CLEAR")}
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {viewMode === "users" && (
          <div className="drill-list">
            <h4 className="drill-list__title">
              Bu makineyi Kullanılan Kullanıcılar
            </h4>
            {loadingList ? (
              <div className="loading-container">
                <FiRefreshCw className="spin" size={48} />
                <p>Yükleniyor...</p>
              </div>
            ) : (
              <div className="drill-list__grid">
                {usersList.map((u) => (
                  <div
                    key={u.id}
                    className="activity-item-card drill-list__card"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedUserId(u.id);
                      setViewMode("activities");
                      setCurrentPage(1);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setSelectedUserId(u.id);
                        setViewMode("activities");
                        setCurrentPage(1);
                      }
                    }}
                  >
                    <FiUser size={20} />
                    <div>
                      <strong>{u.name || "-"}</strong>
                      {u.email && <small className="d-block">{u.email}</small>}
                    </div>
                    <div className="drill-list__right">
                      <div className="drill-list__count" variant="default">
                        {Number(u.recordCount || 0)}
                      </div>
                      <FiChevronRight size={18} className="drill-list__arrow" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loadingList && usersList.length === 0 && (
              <div className="empty-state">Kullanıcı bulunamadı</div>
            )}
          </div>
        )}

        {viewMode === "machines" && (
          <div className="drill-list">
            <h4 className="drill-list__title">
              Bu kullanıcının Kullanıdığı Makineler
            </h4>
            {loadingList ? (
              <div className="loading-container">
                <FiRefreshCw className="spin" size={48} />
                <p>Yükleniyor...</p>
              </div>
            ) : (
              <div className="drill-list__grid">
                {machinesList.map((m) => (
                  <div
                    key={m.id}
                    className="activity-item-card drill-list__card"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setSelectedMachineId(m.id);
                      setViewMode("activities");
                      setCurrentPage(1);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        setSelectedMachineId(m.id);
                        setViewMode("activities");
                        setCurrentPage(1);
                      }
                    }}
                  >
                    <FiServer size={20} />
                    <div>
                      <strong>{m.name || "-"}</strong>
                      {m.code && (
                        <small className="d-block">Kod: {m.code}</small>
                      )}
                    </div>
                    <div className="drill-list__right">
                      <Lozenge className="drill-list__count" variant="default">
                        {Number(m.recordCount || 0)}
                      </Lozenge>
                      <FiChevronRight size={18} className="drill-list__arrow" />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {!loadingList && machinesList.length === 0 && (
              <div className="empty-state">Makine bulunamadı</div>
            )}
          </div>
        )}

        {viewMode === "activities" && loading && activities.length === 0 ? (
          <div className="loading-container">
            <FiRefreshCw className="spin" size={48} />
            <p>Yükleniyor...</p>
          </div>
        ) : viewMode === "activities" ? (
          <>
            {entity && (
              <div className="entity-info-card">
                <div className="entity-info-card__header">
                  <div className="entity-info-card__avatar">
                    {type === "user" ? (
                      <FiUser size={24} />
                    ) : (
                      <FiServer size={24} />
                    )}
                  </div>
                  <div className="entity-info-card__content">
                    <h3>
                      {entity.name ||
                        (type === "user" ? "Kullanıcı" : "Makine")}
                    </h3>
                    {type === "user" && entity.email && <p>{entity.email}</p>}
                    {type === "machine" && (
                      <>
                        {entity.code && <p>Kod: {entity.code}</p>}
                        {entity.location && <p>Konum: {entity.location}</p>}
                      </>
                    )}
                  </div>
                  <div className="entity-info-card__actions">
                    <div className="entity-count-pill" title="Kayıt sayısı">
                      <span className="entity-count-pill__label">Kayıt</span>
                      <span className="entity-count-pill__value">
                        {Number(totalItems || activities.length || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="activities-list">
              {activities.map((activity) => {
                const relatedUserId = activity.user?.id || activity.user?._id;
                const relatedMachineId =
                  activity.machine?.id || activity.machine?._id;
                return (
                  <div
                    key={activity.id || activity._id}
                    className="activity-item-card"
                  >
                    <div className="activity-item-card__header">
                      <div className="activity-item-card__timestamp">
                        <FiClock size={16} />
                        <span>{formatDateTime(activity.timestamp)}</span>
                      </div>
                      {/* {activity.activityType && (
                        <Lozenge
                          variant={getActivityTypeVariant(
                            activity.activityType
                          )}
                        >
                          {getActivityTypeLabel(activity.activityType)}
                        </Lozenge>
                      )} */}
                    </div>
                    <div className="activity-item-card__body">
                      {type !== "user" && activity.user && (
                        <div className="activity-item-card__info-row">
                          <div className="activity-item-card__label">
                            <FiUser size={16} />
                            <span>Kullanıcı:</span>
                          </div>
                          <div className="activity-item-card__value">
                            <strong>{activity.user.name || "-"}</strong>
                            {activity.user.email && (
                              <small>{activity.user.email}</small>
                            )}
                            {relatedUserId && (
                              <span
                                className="activity-link-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/activities/user/${relatedUserId}`);
                                }}
                                title="Kullanıcı aktivitelerini gör"
                              >
                                <FiExternalLink size={14} />
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {type !== "machine" && activity.machine && (
                        <div className="activity-item-card__info-row">
                          <div className="activity-item-card__label">
                            <FiServer size={16} />
                            <span>Makine:</span>
                          </div>
                          <div className="activity-item-card__value">
                            <strong>{activity.machine.name || "-"}</strong>
                            {activity.machine.code && (
                              <small>Kod: {activity.machine.code}</small>
                            )}
                            {relatedMachineId && (
                              <span
                                className="activity-link-icon"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(
                                    `/activities/machine/${relatedMachineId}`,
                                  );
                                }}
                                title="Makine aktivitelerini gör"
                              >
                                <FiExternalLink size={14} />
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {(activity.location || activity.machine?.location) && (
                        <div className="activity-item-card__info-row">
                          <div className="activity-item-card__label">
                            <FiMapPin size={16} />
                            <span>Konum:</span>
                          </div>
                          <div className="activity-item-card__value">
                            {activity.location || activity.machine?.location}
                          </div>
                        </div>
                      )}
                      {activity.notes && (
                        <div className="activity-item-card__info-row">
                          <div className="activity-item-card__label">
                            <FiFileText size={16} />
                            <span>Notlar:</span>
                          </div>
                          <div className="activity-item-card__value">
                            {activity.notes}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {activities.length === 0 && !loading && (
              <div className="empty-state">
                <FiActivity size={64} />
                <p>Aktivite bulunamadı</p>
              </div>
            )}

            {viewMode === "activities" && totalPages > 1 && (
              <div className="pagination-container">
                <div className="pagination-info">
                  <span>
                    {totalItems > 0
                      ? `Sayfa ${currentPage} / ${totalPages} (Toplam ${totalItems} aktivite)`
                      : "Aktivite bulunamadı"}
                  </span>
                </div>
                <nav aria-label="Activity pagination">
                  <ul className="pagination">
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(1)}
                        disabled={currentPage === 1}
                        aria-label="İlk Sayfa"
                        title="İlk Sayfa"
                      >
                        <span aria-hidden="true">«</span>
                      </button>
                    </li>
                    <li
                      className={`page-item ${
                        currentPage === 1 ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                        aria-label="Önceki Sayfa"
                        title="Önceki Sayfa"
                      >
                        <FiChevronLeft size={16} />
                      </button>
                    </li>
                    {getPageNumbers().map((pageNum, index) => {
                      if (pageNum === "…") {
                        return (
                          <li key={`ellipsis-${index}`} className="page-item">
                            <span className="page-link">…</span>
                          </li>
                        );
                      }
                      const isCurrentPage = pageNum === currentPage;
                      return (
                        <li
                          key={pageNum}
                          className={`page-item ${
                            isCurrentPage ? "active" : ""
                          }`}
                        >
                          <button
                            className="page-link"
                            onClick={() => handlePageChange(pageNum)}
                            aria-label={`Sayfa ${pageNum}`}
                            aria-current={isCurrentPage ? "page" : undefined}
                          >
                            {pageNum}
                          </button>
                        </li>
                      );
                    })}
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        aria-label="Sonraki Sayfa"
                        title="Sonraki Sayfa"
                      >
                        <FiChevronRight size={16} />
                      </button>
                    </li>
                    <li
                      className={`page-item ${
                        currentPage === totalPages ? "disabled" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Son Sayfa"
                        title="Son Sayfa"
                      >
                        <span aria-hidden="true">»</span>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </>
        ) : null}
      </CardContainer>

      <ToastContainer>
        {(toasts || []).map((toast) => (
          <Toast
            key={toast.id}
            title={toast.message}
            appearance={toast.type}
            onClose={() =>
              setToasts((toasts || []).filter((t) => t.id !== toast.id))
            }
          />
        ))}
      </ToastContainer>
    </AppPage>
  );
};

export default ActivityDetails;
